import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import AdminUserManagement from "./AdminUserManagement";
import { db } from "../firebase/client";
import { useAuth } from "../contexts/AuthContext";
import { readPlanId, readPlanStatus } from "../services/plan";
import {
  AlertTriangle,
  BadgeDollarSign,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Cpu,
  Loader2,
  Percent,
  Save,
  Server,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Wallet,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanId = "free" | "trial" | "pro" | "premium";

type FinanceSettings = {
  costPerReportAnalysis: number;
  costPerSurgicalPlan: number;
  costPerGuideline: number;
  costPerConsult: number;
  paymentFeePercent: number;
  appStoreFeePercent: number;
  firebaseMonthlyCost: number;
  taxPercent: number;
  otherMonthlyCosts: number;
  avgUsageRatioPro: number;
  avgUsageRatioPremium: number;
};

type FinUserRow = {
  uid: string;
  name: string;
  email: string;
  planTier: PlanId;
  planStatus: string;
  billingInterval: "monthly" | "annual";
  analysesMonthly: number;
  ocrMonthly: number;
  threeDMonthly: number;
  aiEstimatedCostMonthly: number | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: FinanceSettings = {
  costPerReportAnalysis: 0.08,
  costPerSurgicalPlan: 0.10,
  costPerGuideline: 0.05,
  costPerConsult: 0.06,
  paymentFeePercent: 5,
  appStoreFeePercent: 30,
  firebaseMonthlyCost: 50,
  taxPercent: 15,
  otherMonthlyCosts: 30,
  avgUsageRatioPro: 0.6,
  avgUsageRatioPremium: 0.5,
};

const PLAN_PRICE: Record<PlanId, { monthly: number; annual: number }> = {
  free:    { monthly: 0,   annual: 0     },
  trial:   { monthly: 0,   annual: 0     },
  pro:     { monthly: 99,  annual: 990   },
  premium: { monthly: 199, annual: 1990  },
};

const PLAN_LIMITS: Record<PlanId, { analyses: number; ocr: number; threeD: number }> = {
  free:    { analyses: 5,   ocr: 0,   threeD: 3   },
  trial:   { analyses: 50,  ocr: 50,  threeD: 50  },
  pro:     { analyses: 30,  ocr: 30,  threeD: 30  },
  premium: { analyses: 100, ocr: 100, threeD: 100 },
};

const PLAN_COLORS: Record<PlanId, { text: string; badge: string; bar: string }> = {
  free:    { text: "text-slate-400", badge: "bg-slate-700/60 text-slate-300 border-slate-600/40", bar: "bg-slate-500" },
  trial:   { text: "text-blue-400",  badge: "bg-blue-500/15 text-blue-300 border-blue-500/25",   bar: "bg-blue-400"  },
  pro:     { text: "text-cyan-400",  badge: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",   bar: "bg-cyan-400"  },
  premium: { text: "text-yellow-400",badge: "bg-yellow-500/15 text-yellow-300 border-yellow-500/25", bar: "bg-yellow-400" },
};

const PLAN_LABEL: Record<PlanId, string> = { free: "Free", trial: "Trial", pro: "Pro", premium: "Premium" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function brl(v: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 }).format(v);
}

function pct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function marginColor(margin: number): string {
  if (margin >= 60) return "text-emerald-400";
  if (margin >= 40) return "text-amber-400";
  return "text-red-400";
}

/** Compute monthly revenue for a user accounting for annual discount */
function monthlyRevenue(tier: PlanId, interval: "monthly" | "annual"): number {
  const price = PLAN_PRICE[tier];
  if (interval === "annual") return price.annual / 12;
  return price.monthly;
}

/** Compute estimated AI cost for a user based on their usage or ratio estimate */
function computeAiCost(user: FinUserRow, s: FinanceSettings): number {
  if (user.aiEstimatedCostMonthly !== null) return user.aiEstimatedCostMonthly;

  const limits = PLAN_LIMITS[user.planTier] ?? PLAN_LIMITS.free;
  const ratio = user.planTier === "premium" ? s.avgUsageRatioPremium : s.avgUsageRatioPro;

  const ocrUsed = user.ocrMonthly > 0 ? user.ocrMonthly : limits.ocr * ratio;
  const threeD = user.threeDMonthly > 0 ? user.threeDMonthly : limits.threeD * ratio;
  const analyses = user.analysesMonthly > 0 ? user.analysesMonthly : limits.analyses * ratio;

  return (
    ocrUsed * s.costPerReportAnalysis +
    threeD * s.costPerSurgicalPlan +
    analyses * (s.costPerGuideline + s.costPerConsult) / 2
  );
}

/** Net profit for a single paying user */
function userProfit(user: FinUserRow, s: FinanceSettings, totalPaying: number): {
  revenue: number; aiCost: number; paymentFee: number; sharedCosts: number; tax: number; netProfit: number; margin: number;
} {
  const revenue = monthlyRevenue(user.planTier, user.billingInterval);
  const aiCost = computeAiCost(user, s);
  const paymentFee = revenue * (s.paymentFeePercent / 100);
  const sharedCosts = totalPaying > 0
    ? (s.firebaseMonthlyCost + s.otherMonthlyCosts) / totalPaying
    : 0;
  const grossProfit = revenue - aiCost - paymentFee - sharedCosts;
  const tax = Math.max(0, grossProfit * (s.taxPercent / 100));
  const netProfit = grossProfit - tax;
  const margin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
  return { revenue, aiCost, paymentFee, sharedCosts, tax, netProfit, margin };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string; value: string; sub?: string;
  icon: React.ReactNode; accent: string; trend?: "up" | "down" | null;
}> = ({ label, value, sub, icon, accent, trend }) => (
  <div className={`rounded-2xl border bg-slate-900/70 px-5 py-4 ${accent}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
        <p className="mt-1 text-2xl font-black text-white leading-tight">{value}</p>
        {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <div className="opacity-70">{icon}</div>
        {trend === "up" && <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />}
        {trend === "down" && <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
      </div>
    </div>
  </div>
);

const PlanEconomicsCard: React.FC<{
  tier: PlanId; interval: "monthly" | "annual"; settings: FinanceSettings;
}> = ({ tier, interval, settings }) => {
  if (tier === "free" || tier === "trial") return null;
  const limits = PLAN_LIMITS[tier];
  const ratio = tier === "premium" ? settings.avgUsageRatioPremium : settings.avgUsageRatioPro;
  const revenue = interval === "annual" ? PLAN_PRICE[tier].annual / 12 : PLAN_PRICE[tier].monthly;
  const aiMaxCost = (
    limits.ocr * settings.costPerReportAnalysis +
    limits.threeD * settings.costPerSurgicalPlan +
    limits.analyses * (settings.costPerGuideline + settings.costPerConsult) / 2
  );
  const aiAvgCost = aiMaxCost * ratio;
  const paymentFee = revenue * (settings.paymentFeePercent / 100);
  const gross = revenue - aiAvgCost - paymentFee;
  const tax = Math.max(0, gross * (settings.taxPercent / 100));
  const net = gross - tax;
  const margin = revenue > 0 ? (net / revenue) * 100 : 0;
  const colors = PLAN_COLORS[tier];

  const rows: { label: string; value: string; cls?: string }[] = [
    { label: "Receita", value: brl(revenue), cls: "text-white font-bold" },
    { label: "Custo IA (máx)", value: brl(aiMaxCost), cls: "text-red-300" },
    { label: "Custo IA (médio)", value: brl(aiAvgCost), cls: "text-amber-300" },
    { label: "Taxa pagamento", value: brl(paymentFee), cls: "text-slate-300" },
    { label: "Imposto estimado", value: brl(tax), cls: "text-slate-300" },
    { label: "Lucro líquido", value: brl(net), cls: net >= 0 ? "text-emerald-400 font-black" : "text-red-400 font-black" },
    { label: "Margem", value: pct(margin), cls: marginColor(margin) + " font-black" },
  ];

  return (
    <div className={`rounded-2xl border bg-slate-900/70 overflow-hidden ${colors.badge.includes("cyan") ? "border-cyan-500/15" : "border-yellow-500/15"}`}>
      <div className="px-4 py-3 border-b border-slate-800/60 flex items-center justify-between">
        <div>
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${colors.badge}`}>
            {PLAN_LABEL[tier]}
          </span>
          <span className="ml-2 text-[11px] text-slate-500">{interval === "annual" ? "Anual (÷12)" : "Mensal"}</span>
        </div>
        <p className="text-lg font-black text-white">{brl(revenue)}<span className="text-xs text-slate-500">/mês</span></p>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400">{r.label}</span>
            <span className={`text-[12px] ${r.cls || "text-slate-200"}`}>{r.value}</span>
          </div>
        ))}
      </div>
      {/* Margin bar */}
      <div className="px-4 pb-3">
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${margin >= 60 ? "bg-emerald-500" : margin >= 40 ? "bg-amber-500" : "bg-red-500"}`}
            style={{ width: `${Math.max(0, Math.min(100, margin))}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Settings Editor ──────────────────────────────────────────────────────────

type SettingsField = {
  key: keyof FinanceSettings; label: string; prefix?: string; suffix?: string; step?: string;
};

const SETTINGS_FIELDS: SettingsField[] = [
  { key: "costPerReportAnalysis", label: "Custo / Análise de Laudo", prefix: "R$", step: "0.001" },
  { key: "costPerSurgicalPlan", label: "Custo / Plano Cirúrgico", prefix: "R$", step: "0.001" },
  { key: "costPerGuideline", label: "Custo / Diretriz/Guideline", prefix: "R$", step: "0.001" },
  { key: "costPerConsult", label: "Custo / Consulta Patológica", prefix: "R$", step: "0.001" },
  { key: "paymentFeePercent", label: "Taxa de pagamento (cartão)", suffix: "%", step: "0.1" },
  { key: "appStoreFeePercent", label: "Taxa App Store / Google Play", suffix: "%", step: "1" },
  { key: "firebaseMonthlyCost", label: "Custo Firebase mensal", prefix: "R$", step: "1" },
  { key: "taxPercent", label: "Imposto estimado", suffix: "%", step: "0.5" },
  { key: "otherMonthlyCosts", label: "Outros custos mensais fixos", prefix: "R$", step: "1" },
  { key: "avgUsageRatioPro", label: "Uso médio PRO (% do limite)", suffix: "%", step: "0.05" },
  { key: "avgUsageRatioPremium", label: "Uso médio Premium (% do limite)", suffix: "%", step: "0.05" },
];

const SettingsEditor: React.FC<{
  settings: FinanceSettings;
  onChange: (key: keyof FinanceSettings, value: number) => void;
  onSave: () => void;
  saving: boolean;
}> = ({ settings, onChange, onSave, saving }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-bold text-white">Configurações financeiras</span>
          <span className="text-[11px] text-slate-500">Editar custos e taxas</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-800/60 px-5 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            {SETTINGS_FIELDS.map((f) => {
              const raw = settings[f.key];
              const displayVal = f.suffix === "%" && (f.key === "avgUsageRatioPro" || f.key === "avgUsageRatioPremium")
                ? (raw as number) * 100
                : raw as number;
              return (
                <div key={f.key}>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{f.label}</label>
                  <div className="flex items-center gap-1">
                    {f.prefix && <span className="text-xs text-slate-500">{f.prefix}</span>}
                    <input
                      type="number"
                      step={f.step ?? "0.01"}
                      min={0}
                      value={displayVal}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        const stored = f.suffix === "%" && (f.key === "avgUsageRatioPro" || f.key === "avgUsageRatioPremium")
                          ? v / 100
                          : v;
                        onChange(f.key, stored);
                      }}
                      className="w-full bg-slate-800 border border-slate-700 text-slate-100 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-cyan-500/50"
                    />
                    {f.suffix && <span className="text-xs text-slate-500">{f.suffix}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-600/90 hover:bg-cyan-600 disabled:opacity-50 px-4 py-2 text-sm font-bold text-white transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar configurações
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Alert ────────────────────────────────────────────────────────────────────

type FinAlert = { kind: "error" | "warn" | "info"; text: string };

function buildAlerts(
  users: FinUserRow[],
  settings: FinanceSettings,
  totalPaying: number
): FinAlert[] {
  const alerts: FinAlert[] = [];
  const LOW_MARGIN = 50;
  const HIGH_AI_RATIO = 0.35; // AI cost > 35% of revenue

  for (const u of users) {
    if (u.planTier === "free") continue;
    const { revenue, aiCost, margin, netProfit } = userProfit(u, settings, totalPaying);
    if (netProfit < 0) {
      alerts.push({ kind: "error", text: `${u.email || u.name} — plano ${PLAN_LABEL[u.planTier]} está gerando PREJUÍZO (${brl(netProfit)})` });
    } else if (margin < LOW_MARGIN) {
      alerts.push({ kind: "warn", text: `${u.email || u.name} — margem abaixo de ${LOW_MARGIN}% (${pct(margin)})` });
    }
    if (revenue > 0 && aiCost / revenue > HIGH_AI_RATIO) {
      alerts.push({ kind: "warn", text: `${u.email || u.name} — custo de IA alto: ${pct((aiCost / revenue) * 100)} da receita` });
    }
  }

  // Global plan margin check
  for (const tier of ["pro", "premium"] as PlanId[]) {
    for (const interval of ["monthly", "annual"] as const) {
      const rev = interval === "annual" ? PLAN_PRICE[tier].annual / 12 : PLAN_PRICE[tier].monthly;
      if (rev === 0) continue;
      const limits = PLAN_LIMITS[tier];
      const ratio = tier === "premium" ? settings.avgUsageRatioPremium : settings.avgUsageRatioPro;
      const ai = (limits.ocr * settings.costPerReportAnalysis + limits.threeD * settings.costPerSurgicalPlan + limits.analyses * (settings.costPerGuideline + settings.costPerConsult) / 2) * ratio;
      const fee = rev * settings.paymentFeePercent / 100;
      const gross = rev - ai - fee;
      const net = gross - Math.max(0, gross * settings.taxPercent / 100);
      const m = (net / rev) * 100;
      if (m < LOW_MARGIN) {
        alerts.push({ kind: "warn", text: `Plano ${PLAN_LABEL[tier]} ${interval === "annual" ? "Anual" : "Mensal"} com margem baixa: ${pct(m)}` });
      }
    }
  }

  if (alerts.length === 0) {
    alerts.push({ kind: "info", text: "Todos os planos ativos estão com margem saudável." });
  }

  return alerts.slice(0, 10);
}

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminFinancePanel: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<FinUserRow[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [settings, setSettings] = useState<FinanceSettings>(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [sortBy, setSortBy] = useState<"margin" | "revenue" | "aiCost">("margin");
  const [filterPlan, setFilterPlan] = useState<string>("all");

  // Load settings from Firestore
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "adminFinanceSettings", "main"));
        if (snap.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...snap.data() as Partial<FinanceSettings> });
        }
      } catch (e) {
        console.error("[Finance][settings:load]", e);
      }
    };
    void load();
  }, []);

  // Load users
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setUsers(snap.docs.map((d) => {
        const raw = d.data();
        const tier = readPlanId(raw) as PlanId;
        const status = readPlanStatus(raw);
        const usage = raw.usage || raw.usageState || {};
        // billing interval detection
        const rawPlan = raw.plan || {};
        const interval: "monthly" | "annual" =
          rawPlan.billing === "annual" || rawPlan.interval === "annual" ? "annual" : "monthly";
        return {
          uid: d.id,
          name: raw.profile?.name || raw.name || "Sem nome",
          email: raw.profile?.email || raw.email || "",
          planTier: tier,
          planStatus: status,
          billingInterval: interval,
          analysesMonthly: Number(usage.analysesMonthly ?? raw.analysesMonthly ?? 0),
          ocrMonthly: Number(usage.ocrMonthly ?? raw.ocrMonthly ?? 0),
          threeDMonthly: Number(usage.threeDMonthly ?? raw.threeDMonthly ?? 0),
          aiEstimatedCostMonthly: raw.aiEstimatedCostMonthly != null ? Number(raw.aiEstimatedCostMonthly) : null,
        };
      }));
      setLoadingUsers(false);
    }, () => setLoadingUsers(false));
    return () => unsub();
  }, [user]);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      await setDoc(doc(db, "adminFinanceSettings", "main"), { ...settings, updatedAt: serverTimestamp() });
      setSaveMsg("Salvo ✓");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      console.error("[Finance][settings:save]", e);
      setSaveMsg("Erro ao salvar");
    } finally {
      setSavingSettings(false);
    }
  }

  function updateSetting(key: keyof FinanceSettings, value: number) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  // ─ Computed data ─
  const payingUsers = useMemo(() =>
    users.filter((u) => u.planTier !== "free" && (u.planStatus === "active" || u.planStatus === "trialing")),
    [users]
  );

  const totalPaying = payingUsers.length;

  const summary = useMemo(() => {
    let grossRevenue = 0;
    let totalAiCost = 0;
    let totalPaymentFees = 0;
    let totalTax = 0;
    let totalNet = 0;

    for (const u of payingUsers) {
      const { revenue, aiCost, paymentFee, tax, netProfit } = userProfit(u, settings, totalPaying);
      grossRevenue += revenue;
      totalAiCost += aiCost;
      totalPaymentFees += paymentFee;
      totalTax += tax;
      totalNet += netProfit;
    }

    const infraCost = settings.firebaseMonthlyCost + settings.otherMonthlyCosts;
    const netAfterInfra = totalNet;
    const margin = grossRevenue > 0 ? (netAfterInfra / grossRevenue) * 100 : 0;

    return { grossRevenue, totalAiCost, totalPaymentFees, totalTax, infraCost, netAfterInfra, margin };
  }, [payingUsers, settings, totalPaying]);

  const alerts = useMemo(() => buildAlerts(payingUsers, settings, totalPaying), [payingUsers, settings, totalPaying]);

  // ─ User table data ─
  const tableData = useMemo(() => {
    const data = (filterPlan === "all" ? payingUsers : payingUsers.filter((u) => u.planTier === filterPlan))
      .map((u) => {
        const fin = userProfit(u, settings, totalPaying);
        return { user: u, ...fin };
      });

    return data.sort((a, b) => {
      if (sortBy === "margin") return b.margin - a.margin;
      if (sortBy === "revenue") return b.revenue - a.revenue;
      return b.aiCost - a.aiCost;
    });
  }, [payingUsers, settings, totalPaying, sortBy, filterPlan]);

  // ─ Plan breakdown ─
  const planBreakdown = useMemo(() => {
    const counts: Record<PlanId, number> = { free: 0, trial: 0, pro: 0, premium: 0 };
    for (const u of users) {
      if (u.planTier in counts) counts[u.planTier]++;
    }
    return counts;
  }, [users]);

  const selectCls = "bg-slate-800/70 border border-slate-700/60 text-slate-300 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-cyan-500/50";

  if (loadingUsers) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Carregando dados financeiros...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ─ Settings ─ */}
      <SettingsEditor settings={settings} onChange={updateSetting} onSave={saveSettings} saving={savingSettings} />
      {saveMsg && (
        <p className={`text-sm font-bold ${saveMsg.startsWith("Erro") ? "text-red-400" : "text-emerald-400"}`}>{saveMsg}</p>
      )}

      {/* ─ Summary cards ─ */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
          Visão geral mensal — {totalPaying} assinante{totalPaying !== 1 ? "s" : ""} ativos
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard
            label="Receita bruta" value={brl(summary.grossRevenue)}
            sub={`${users.length} usuários total`}
            icon={<CircleDollarSign className="w-6 h-6 text-emerald-400" />}
            accent="border-emerald-500/20" trend="up"
          />
          <MetricCard
            label="Custo de IA" value={brl(summary.totalAiCost)}
            sub={`${pct(summary.grossRevenue > 0 ? (summary.totalAiCost / summary.grossRevenue) * 100 : 0)} da receita`}
            icon={<Cpu className="w-6 h-6 text-red-400" />}
            accent="border-red-500/20"
          />
          <MetricCard
            label="Infraestrutura" value={brl(summary.infraCost)}
            sub="Firebase + outros"
            icon={<Server className="w-6 h-6 text-slate-400" />}
            accent="border-slate-700/50"
          />
          <MetricCard
            label="Taxas plataforma" value={brl(summary.totalPaymentFees)}
            sub={`${settings.paymentFeePercent}% cartão`}
            icon={<BadgeDollarSign className="w-6 h-6 text-amber-400" />}
            accent="border-amber-500/20"
          />
          <MetricCard
            label="Lucro líquido" value={brl(summary.netAfterInfra)}
            sub="após impostos"
            icon={<Wallet className="w-6 h-6 text-cyan-400" />}
            accent="border-cyan-500/20"
            trend={summary.netAfterInfra >= 0 ? "up" : "down"}
          />
          <MetricCard
            label="Margem líquida" value={pct(summary.margin)}
            sub={summary.margin >= 60 ? "Saudável" : summary.margin >= 40 ? "Atenção" : "Crítico"}
            icon={<Percent className="w-6 h-6" style={{ color: summary.margin >= 60 ? "#34d399" : summary.margin >= 40 ? "#fbbf24" : "#f87171" }} />}
            accent={summary.margin >= 60 ? "border-emerald-500/20" : summary.margin >= 40 ? "border-amber-500/20" : "border-red-500/20"}
          />
        </div>
      </div>

      {/* ─ Plan economics ─ */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
          Economia por assinatura
          <span className="ml-2 text-slate-600 normal-case font-normal">Pro: {planBreakdown.pro} · Premium: {planBreakdown.premium} · Trial: {planBreakdown.trial} · Free: {planBreakdown.free}</span>
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <PlanEconomicsCard tier="pro" interval="monthly" settings={settings} />
          <PlanEconomicsCard tier="pro" interval="annual" settings={settings} />
          <PlanEconomicsCard tier="premium" interval="monthly" settings={settings} />
          <PlanEconomicsCard tier="premium" interval="annual" settings={settings} />
        </div>
      </div>

      {/* ─ Alerts ─ */}
      <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-800/60 flex items-center gap-2">
          <TriangleAlert className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-black text-white">Alertas financeiros</span>
        </div>
        <div className="divide-y divide-slate-800/40">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-start gap-3 px-5 py-3">
              {a.kind === "error" && <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />}
              {a.kind === "warn" && <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />}
              {a.kind === "info" && <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />}
              <p className={`text-sm ${a.kind === "error" ? "text-red-300" : a.kind === "warn" ? "text-amber-300" : "text-emerald-300"}`}>
                {a.text}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ─ User management ─ */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
          Gerenciamento de usuários e assinaturas
        </p>
        <AdminUserManagement />
      </div>

      {/* ─ User financial breakdown table ─ */}
      <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800/60 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-black text-white">Breakdown por usuário</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">{tableData.length} assinante{tableData.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-2">
            <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} className={selectCls}>
              <option value="all">Todos os planos</option>
              <option value="trial">Trial</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className={selectCls}>
              <option value="margin">Ordenar: Margem</option>
              <option value="revenue">Ordenar: Receita</option>
              <option value="aiCost">Ordenar: Custo IA</option>
            </select>
          </div>
        </div>

        {tableData.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">Nenhum assinante ativo encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-800/60">
                  {["Usuário", "Plano", "Receita", "Custo IA", "Taxas", "Custos fixos", "Imposto", "Lucro líquido", "Margem"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {tableData.map(({ user: u, revenue, aiCost, paymentFee, sharedCosts, tax, netProfit, margin }) => {
                  const colors = PLAN_COLORS[u.planTier];
                  return (
                    <tr key={u.uid} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-2.5 max-w-36">
                        <p className="font-medium text-slate-200 truncate">{u.name}</p>
                        <p className="text-slate-500 truncate">{u.email}</p>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex rounded-full border px-1.5 py-0.5 text-[9px] font-black uppercase ${colors.badge}`}>
                          {PLAN_LABEL[u.planTier]}
                        </span>
                        <span className="block text-[9px] text-slate-600 mt-0.5">{u.billingInterval}</span>
                      </td>
                      <td className="px-4 py-2.5 text-emerald-400 font-semibold whitespace-nowrap">{brl(revenue)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={aiCost / revenue > 0.35 ? "text-red-400 font-bold" : "text-slate-300"}>{brl(aiCost)}</span>
                        {u.aiEstimatedCostMonthly !== null && (
                          <span className="block text-[9px] text-cyan-600">real</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{brl(paymentFee)}</td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{brl(sharedCosts)}</td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{brl(tax)}</td>
                      <td className={`px-4 py-2.5 font-black whitespace-nowrap ${netProfit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {brl(netProfit)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`font-black ${marginColor(margin)}`}>{pct(margin)}</span>
                        <div className="w-12 h-1 rounded-full bg-slate-800 mt-1 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${margin >= 60 ? "bg-emerald-500" : margin >= 40 ? "bg-amber-500" : "bg-red-500"}`}
                            style={{ width: `${Math.max(0, Math.min(100, margin))}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFinancePanel;
